/**
 * Utilidad de enlaces a la Sede Electrónica del Catastro
 *
 * Genera URLs directas a fichas de bienes inmuebles mediante referencia catastral
 * o mediante coordenadas WGS84 como fallback cuando no se dispone de refcat.
 *
 * Nota: C3.03 garantiza que los `refcat` llegan limpios desde R2 (sin espacios),
 * pero las funciones aplican normalización defensiva igualmente.
 *
 * @module utils/catastroUrl
 * @version 1.1.0 — UI-P3: buildCatastroUrlByCoords + buildCatastroHref (enlace dual)
 */

import { fromUtm } from './projections';

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Base de la Sede Electrónica del Catastro.
 * OVCListaBienes acepta rc1 (primeros 7) + rc2 (siguientes 7) y redirige
 * automáticamente a la ficha completa independientemente del municipio.
 * OVCConCiud.aspx requiere del+mun y falla sin ellos → NO usar.
 */
const SEDE_CATASTRO_BASE =
  'https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCListaBienes.aspx';

/**
 * Base del visor cartográfico de la Sede del Catastro.
 * Acepta ?buscar=G&x={lng}&y={lat} (WGS84 decimal) y muestra la parcela
 * más cercana a las coordenadas indicadas. Usado como fallback cuando no
 * se dispone de referencia catastral directa.
 */
const SEDE_CARTOGRAFIA_BASE =
  'https://www1.sedecatastro.gob.es/cartografia/mapa.aspx';

/** Longitud exacta de una referencia catastral normalizada */
const REFCAT_LENGTH = 14;

/** Patrón válido: 14 caracteres alfanuméricos (A-Z, 0-9) */
const REFCAT_PATTERN = /^[A-Z0-9]{14}$/;

// ============================================================================
// FUNCIONES PÚBLICAS
// ============================================================================

/**
 * Construye la URL directa a la ficha del bien inmueble en la Sede del Catastro.
 *
 * La normalización elimina espacios y convierte a mayúsculas, aunque desde C3.03
 * los refcat ya llegan sin espacios. Se mantiene por robustez defensiva.
 *
 * @param refcat - Referencia catastral (con o sin espacios embebidos)
 * @returns URL completa lista para abrir en navegador (`target="_blank"`)
 *
 * @example
 * buildCatastroUrl('110154030037')
 * // → 'https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCConCiud.aspx?RefC=110154030037'
 *
 * buildCatastroUrl('11015  4030037')  // espacios legacy
 * // → 'https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCConCiud.aspx?RefC=110154030037'
 */
export function buildCatastroUrl(refcat: string): string {
  const rc = refcat.replace(/\s+/g, '').toUpperCase();
  const rc1 = rc.slice(0, 7);
  const rc2 = rc.slice(7, 14);
  return `${SEDE_CATASTRO_BASE}?rc1=${rc1}&rc2=${rc2}`;
}

/**
 * Valida si una cadena es una referencia catastral legal.
 *
 * Una referencia catastral válida tiene exactamente 14 caracteres alfanuméricos
 * (letras A-Z y dígitos 0-9) tras eliminar espacios y convertir a mayúsculas.
 *
 * @param refcat - Cadena a validar
 * @returns `true` si es una referencia catastral válida
 *
 * @example
 * isValidRefcat('110154030037')    // → false (12 chars)
 * isValidRefcat('11015A0300045XY') // → true  (14 chars, formato rústico)
 * isValidRefcat('11015  4030037X') // → true  (14 chars con espacios legacy)
 * isValidRefcat('')                // → false
 */
export function isValidRefcat(refcat: string): boolean {
  if (!refcat) return false;
  return REFCAT_PATTERN.test(refcat.replace(/\s+/g, '').toUpperCase());
}

// ============================================================================
// EXPORTACIONES DE TIPO (para uso en tests y componentes)
// ============================================================================

/** Longitud canónica de un refcat limpio */
export { REFCAT_LENGTH };

// ============================================================================
// ENLACE POR COORDENADAS (fallback sin refcat)
// ============================================================================

/**
 * Construye la URL del visor cartográfico de la Sede del Catastro a partir
 * de coordenadas UTM30 ETRS89 (EPSG:25830).
 *
 * Convierte internamente a WGS84 usando proj4 y genera la URL del visor
 * de cartografía catastral, que muestra la parcela más cercana al punto.
 *
 * @param x - Coordenada Este UTM30 (metros, EPSG:25830)
 * @param y - Coordenada Norte UTM30 (metros, EPSG:25830)
 * @returns URL del visor cartográfico lista para `target="_blank"`
 *
 * @example
 * buildCatastroUrlByCoords(449123, 4122345)
 * // → 'https://www1.sedecatastro.gob.es/cartografia/mapa.aspx?buscar=G&x=-3.6824&y=37.2341'
 */
export function buildCatastroUrlByCoords(x: number, y: number): string {
  const wgs = fromUtm(x, y, 'EPSG:25830');
  const lng = wgs.lng.toFixed(6);
  const lat = wgs.lat.toFixed(6);
  return `${SEDE_CARTOGRAFIA_BASE}?buscar=G&x=${lng}&y=${lat}`;
}

/**
 * Selector de enlace catastral dual (Opción C).
 *
 * Prioridad:
 *   1. Si `refcat` es válido → ficha exacta del inmueble (OVCListaBienes)
 *   2. Si hay coordenadas UTM30 → visor cartográfico por proximidad (fallback)
 *   3. Sin datos suficientes → `null` (no mostrar enlace)
 *
 * @param opts.refcat - Referencia catastral opcional (14 chars)
 * @param opts.x     - Coordenada Este UTM30 (metros)
 * @param opts.y     - Coordenada Norte UTM30 (metros)
 * @returns URL lista para `href`, o `null` si no hay datos suficientes
 */
/**
 * Comprueba que una coordenada UTM es utilizable para construir un enlace.
 * Rechaza: undefined, null, NaN, Infinity, y 0 (centroide fallido).
 */
function isUsableCoord(v: number | undefined | null): v is number {
  return typeof v === 'number' && isFinite(v) && v !== 0;
}

export function buildCatastroHref(opts: {
  refcat?: string;
  x?: number;
  y?: number;
}): string | null {
  if (opts.refcat && isValidRefcat(opts.refcat)) {
    return buildCatastroUrl(opts.refcat);
  }
  // BUG-UI-17 FIX: filtrar coords inválidas (NaN, 0, Infinity) antes de llamar
  // a buildCatastroUrlByCoords — fromUtm lanza si x/y no son finitos.
  if (isUsableCoord(opts.x) && isUsableCoord(opts.y)) {
    try {
      return buildCatastroUrlByCoords(opts.x, opts.y);
    } catch {
      // Coords fuera del elipsoide o EPSG no soportado → no mostrar enlace
      return null;
    }
  }
  return null;
}
